import type { SupabaseClient } from "@supabase/supabase-js";
import { db, cardStateKey, formKey, makeDirection } from "../db";
import type { Concept, ConceptForm, CardState, DeckRow } from "../db";
import type { Database } from "../database.types";

type RemoteDeck = Database["public"]["Tables"]["decks"]["Row"];
type RemoteConcept = Database["public"]["Tables"]["concepts"]["Row"];
type RemoteConceptForm = Database["public"]["Tables"]["concept_forms"]["Row"];

/**
 * Local id of an assigned deck after import.
 *
 * Cloud keys are per-owner ((owner_id, id)), Dexie keys are global, and deck
 * ids are partly deterministic ("demo-deck-1"), so a tutor's deck id may
 * legitimately equal one the student already owns. Re-keying imported content
 * under a per-tutor namespace is the smallest change that keeps Dexie keys
 * single-column: without it, the existence check below reads the student's own
 * deck and the import is silently skipped — or worse, overwrites it.
 */
export function localAssignedDeckId(tutorId: string, remoteDeckId: string): string {
  return `tutor:${tutorId}:${remoteDeckId}`;
}

/**
 * Same namespacing for concepts (demo concepts have deterministic ids too).
 * The "user:" head is kept because store.tsx and the sync engine use it to
 * recognise content that belongs to the student and must be pushed/deleted
 * together with its card — which is what an imported concept becomes.
 */
function localConceptId(tutorId: string, remoteConceptId: string): string {
  return `user:tutor:${tutorId}:${remoteConceptId}`;
}

/**
 * Import a tutor-assigned deck and its concepts into local Dexie. Idempotent.
 *
 * @param supabaseClient - injected so callers (and tests) can supply any client
 * @param tutorId - the tutor's user_id; used to scope the concepts query and
 *   prevent pack_id collisions across users
 */
export async function importAssignedDeck(
  supabaseClient: SupabaseClient<Database>,
  tutorId: string,
  remoteDeck: RemoteDeck,
): Promise<{ imported: boolean }> {
  const deckId = localAssignedDeckId(tutorId, remoteDeck.id);
  const existingDeck = await db.decks.get(deckId);
  if (existingDeck) return { imported: false }; // already imported

  // Fetch concepts belonging to this deck's owner that are in this deck.
  // The owner_id filter prevents pack_id collisions between different users.
  const { data: remoteConcepts } = await supabaseClient
    .from("concepts")
    .select("*")
    .eq("pack_id", `user:${remoteDeck.id}`)
    .eq("owner_id", tutorId);

  // concept ids are unique per owner only, so the owner has to be pinned here
  // as well — otherwise a stranger's form with the same concept_id matches.
  const { data: remoteForms } = await supabaseClient
    .from("concept_forms")
    .select("*")
    .eq("owner_id", tutorId)
    .in(
      "concept_id",
      (remoteConcepts ?? []).map((c) => c.id),
    );

  const now = Date.now();
  const deckRow: DeckRow = {
    id: deckId,
    name: remoteDeck.name ?? "Без названия",
    createdAt: now,
    sourceLang: remoteDeck.source_lang as DeckRow["sourceLang"] ?? undefined,
    targetLang: remoteDeck.target_lang as DeckRow["targetLang"] ?? undefined,
    topic: remoteDeck.topic as DeckRow["topic"] ?? undefined,
    ageGroup: remoteDeck.age_group as DeckRow["ageGroup"] ?? undefined,
    level: remoteDeck.level as DeckRow["level"] ?? undefined,
  };

  const concepts: Concept[] = (remoteConcepts ?? []).map((rc: RemoteConcept) => ({
    id: localConceptId(tutorId, rc.id),
    entityType: (rc.entity_type ?? "word") as Concept["entityType"],
    frequency: rc.frequency ?? undefined,
    topic: rc.topic as Concept["topic"] ?? undefined,
    ageGroup: rc.age_group as Concept["ageGroup"] ?? undefined,
    level: rc.level as Concept["level"] ?? undefined,
    packId: `user:${deckId}`,
  }));

  // Forms are re-keyed onto the local concept ids; a form without a
  // concept_id has no key to derive and is dropped.
  const forms: ConceptForm[] = (remoteForms ?? [])
    .filter((rf: RemoteConceptForm) => rf.concept_id !== null)
    .map((rf: RemoteConceptForm) => {
      const conceptId = localConceptId(tutorId, rf.concept_id as string);
      const lang = (rf.lang ?? "und") as ConceptForm["lang"];
      return { key: formKey(conceptId, lang), conceptId, lang, texts: rf.texts ?? [] };
    });

  const srcLang = remoteDeck.source_lang;
  const tgtLang = remoteDeck.target_lang;

  // cardState existence check and state building are inside the rw-transaction
  // to eliminate the TOCTOU race between the check and the write.
  await db.transaction("rw", [db.decks, db.concepts, db.conceptForms, db.cardStates], async () => {
    await db.decks.put(deckRow);
    if (concepts.length > 0) await db.concepts.bulkPut(concepts);
    if (forms.length > 0) await db.conceptForms.bulkPut(forms);

    if (srcLang && tgtLang) {
      const direction = makeDirection(
        srcLang as Parameters<typeof makeDirection>[0],
        tgtLang as Parameters<typeof makeDirection>[1],
      );
      for (const c of concepts) {
        const key = cardStateKey(c.id, direction);
        const existingState = await db.cardStates.get(key);
        if (!existingState) {
          const state: CardState = {
            key,
            conceptId: c.id,
            direction,
            deckId,
            nextReview: now,
            interval: 0,
            repetition: 0,
            efactor: 2.5,
          };
          await db.cardStates.put(state);
        }
      }
    }
  });

  return { imported: true };
}
